import React from 'react';
export type PillTabItem = {
  id: string;
  label: string;
  /** Optional brand logo URL, rendered to the left of the label. */
  logo?: string;
  ariaLabel?: string;
};
/**
 * The small, quiet segmented control used across /alt. Deliberately lighter
 * than the page's pillar navigation: these are local controls, so they sit in
 * a single bordered pill with the active item filled in ink.
 *
 * `value` may be null when the surface is in a state no tab represents (for
 * example the architecture diagram after individual blocks were toggled).
 */
export function PillTabs({
  items,
  value,
  onChange,
  ariaLabel,
  className = '',
  equal = false
}: {
  items: PillTabItem[];
  value: string | null;
  onChange: (id: string) => void;
  ariaLabel: string;
  className?: string;
  /**
   * Lay the items out on an EVEN grid instead of packing them to their own
   * copy. With labels as different in length as "Slack" and "ChatGPT", intrinsic
   * widths leave the segments visibly unequal and the gaps between logos
   * irregular — the single thing that makes the control read as assembled
   * rather than designed. On a grid every segment is the width of the widest,
   * so the marks sit on an even rhythm.
   */
  equal?: boolean;
}) {
  // The track is recessed (inset shadow) and the active pill is raised, so the
  // control reads as a physical switch rather than two flat rectangles.
  return <div className={`${equal ? 'inline-grid auto-cols-fr grid-flow-col gap-1' : 'inline-flex flex-wrap gap-1.5'} items-center justify-center rounded-full border border-[#E7E0D4] bg-[#FBF8F3] p-1 shadow-[inset_0_1px_2px_rgba(43,33,26,0.05)] ${className}`} role="tablist" aria-label={ariaLabel} data-id="element-885" data-mp="2hwr0ukbxnagh">
      {items.map(item => {
      const active = item.id === value;
      return <button key={item.id} type="button" role="tab" aria-selected={active} aria-label={item.ariaLabel} onClick={() => onChange(item.id)} className={`inline-flex items-center justify-center rounded-full font-semibold transition-[background-color,color,box-shadow] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]/45 focus-visible:ring-offset-1 focus-visible:ring-offset-[#FBF8F3] ${equal ? 'gap-2 px-5 py-2.5 text-[13.5px] tracking-[-0.005em]' : 'gap-1.5 px-3 py-1.5 text-[13px]'} ${active ? 'bg-[#2B211A] text-white shadow-[0_2px_6px_-1px_rgba(43,33,26,0.45)]' : 'text-[#6B5D52] hover:bg-white hover:text-[#2B211A] hover:shadow-[0_1px_2px_rgba(43,33,26,0.06)]'}`} data-id="element-886" data-mp="xhp4f0ak1muy">
            {item.logo && <img src={item.logo} alt="" aria-hidden="true" className={`shrink-0 object-contain ${equal ? 'size-[17px]' : 'size-[15px]'}`} data-id="element-887" data-mp="2565noh1gekyu" />}
            {item.label}
          </button>;
    })}
    </div>;
}